// First-fit-decreasing 1D bin-pack ported from the mockup
// (production-schedule.html lines 1124–1153). Pure function — no
// React, no DOM. Phase 7 wires the UI; this lives here so vitest
// can cover it independently.

export interface OptimizerCut {
  label: string;
  length: number;
  note?: string;
}

export interface OptimizerInput {
  stockLength: number;
  kerf: number;
  cuts: { label: string; length: number; qty: number; note?: string }[];
}

export interface OptimizerBin {
  cuts: OptimizerCut[];
  used: number;
  remaining: number;
}

export interface OptimizerResult {
  bins: OptimizerBin[];
  totalCuts: number;
  totalLength: number;
  stockUsed: number;
  efficiencyPct: number;
}

export function binPack(input: OptimizerInput): OptimizerResult {
  const { stockLength, kerf } = input;
  const expanded: OptimizerCut[] = [];
  for (const row of input.cuts) {
    const len = Math.max(0, Math.floor(row.length || 0));
    const qty = Math.max(0, Math.floor(row.qty || 0));
    if (!len || !qty) continue;
    for (let i = 0; i < qty; i += 1) {
      expanded.push({ label: row.label || "cut", length: len, note: row.note });
    }
  }
  expanded.sort((a, b) => b.length - a.length);

  const bins: { cuts: OptimizerCut[] }[] = [];
  for (const cut of expanded) {
    let placed = false;
    for (const bin of bins) {
      const used =
        bin.cuts.reduce((acc, x) => acc + x.length, 0) +
        Math.max(0, bin.cuts.length - 1) * kerf;
      // After placing this cut, total includes one more kerf gap.
      const projected = used + (bin.cuts.length > 0 ? kerf : 0) + cut.length;
      if (projected <= stockLength) {
        bin.cuts.push(cut);
        placed = true;
        break;
      }
    }
    if (!placed) bins.push({ cuts: [cut] });
  }

  const totalLength = expanded.reduce((acc, c) => acc + c.length, 0);
  const stockUsed = bins.length * stockLength;
  const efficiencyPct =
    stockUsed > 0 ? Math.round((totalLength / stockUsed) * 1000) / 10 : 0;
  return {
    bins: bins.map((bin) => {
      const used =
        bin.cuts.reduce((acc, c) => acc + c.length, 0) +
        Math.max(0, bin.cuts.length - 1) * kerf;
      return {
        cuts: bin.cuts,
        used,
        remaining: stockLength - used,
      };
    }),
    totalCuts: expanded.length,
    totalLength,
    stockUsed,
    efficiencyPct,
  };
}
